import type { Availability, BBox } from '../api'
import { backgroundTileUrl, type BackgroundLayer } from './background'

export type PollResult = 'ready' | 'timeout' | 'aborted'

/** Опрос готовности тайлов ПОСЛЕ «Загрузить» на «Моделях».
 *
 *  Тайлы shared/upwelling рисуются воркером АСИНХРОННО — задача докачки
 *  помечается completed раньше, чем PNG лягут на диск. Одноразовый показ в
 *  onReady попадал на прозрачные/серые заглушки и больше не обновлялся: цветные
 *  тайлы появлялись только после жёсткой перезагрузки. Здесь опрашиваем
 *  availability до `tiles_ready`, затем применяем ВЕРСИОНИРОВАННЫЙ URL (метка
 *  tiles_version делает адрес иммутабельным и разом сменяет заглушку на цветной
 *  тайл). Зеркалит прогрессивный опрос «Карт».
 *
 *  Чистая по зависимостям: сеть/таймер/карта приходят колбэками (тестируемо).
 */
export async function pollTilesReady(opts: {
  layer: BackgroundLayer
  date: string
  depth: number
  availability: (p: { layer: string; date: string; depth: number; bbox: BBox;
                      species_id?: number | null }) => Promise<Availability>
  bbox: () => BBox
  applyVersioned: (url: string) => void
  sleep: (ms: number) => Promise<void>
  alive: () => boolean
  intervalMs?: number
  maxTicks?: number
  /** Код палитры домена; пусто — наша гамма и прежний адрес. */
  palette?: string | null
  /** Вид — нужен слоям, считающимся ПО ВИДУ (`nn`). Безвидовым не мешает. */
  species?: number | null
}): Promise<PollResult> {
  const interval = opts.intervalMs ?? 2000
  const max = opts.maxTicks ?? 45 // ~90 с потолок ожидания генерации
  // У апвеллинга глубины нет — его ленивый эндпоинт без depth (см. backgroundTileUrl).
  const d = opts.layer === 'upwelling' ? 0 : opts.depth

  for (let n = 0; n < max; n++) {
    if (!opts.alive()) return 'aborted'
    let av: Availability | null = null
    try {
      av = await opts.availability({
        layer: opts.layer, date: opts.date, depth: d, bbox: opts.bbox(),
        species_id: opts.species ?? null,
      })
    } catch {
      av = null // сеть моргнула — следующий тик повторит
    }
    if (!opts.alive()) return 'aborted'
    if (av && av.tiles_ready) {
      const url = backgroundTileUrl(
        opts.layer, opts.date, d, av.tiles_version ?? null, opts.palette,
        opts.species,
      )
      // Адреса нет — у видового слоя не спросили вид. Применять тут нечего, и
      // пустая строка сняла бы живой слой (см. `backgroundTileUrl`).
      if (url) opts.applyVersioned(url)
      return 'ready'
    }
    await opts.sleep(interval)
  }
  return 'timeout'
}
